/**
 * An element form's draft validation: the findings the drawing would hold if Save sent the
 * form's body now. The form calls `requestDraft` with the body its Save builds on each field
 * blur, and on each commit `useDraftOnCommit` reports. A body equal to the last one sent sends
 * nothing, and an answer that arrives after a newer request, or after the draft was dropped, is
 * ignored.
 *
 * `isValidating` is true from the moment a request is sent until its answer is applied, or until
 * the draft is dropped or a drawing write supersedes the request; the form shows the findings it
 * holds meanwhile as stale.
 *
 * The draft is dropped (the form shows the saved findings again) by `discardDraft`, and when
 * `enabled` turns false, as it does when a version preview starts. A write to the drawing drops
 * any answer still in flight, since that answer was computed against the drawing the write
 * replaced, but the draft itself stands until the form's `savedFindings` change: until then the
 * saved findings are the ones read before the write, and after a Save the draft is the closer
 * match to what the write stored. The next commit computes a draft again.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { onDrawingWrite } from '../api';
import type { DraftValidation } from '../types';

/** One draft call: the body Save would send, and the validate-draft POST that sends it. */
export interface DraftRequest {
  body: object;
  send: () => Promise<DraftValidation>;
}

/** The validate-draft answers that refuse the body as the PUT would: a duplicate tag (409)
 *  and a line's duct/geometry conflict (400). The form shows the message as Save would. */
const REFUSING_STATUSES = new Set([400, 409]);

function refusalMessage(error: unknown): string | null {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (!response?.status || !REFUSING_STATUSES.has(response.status)) return null;
  const detail = response.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'message' in detail) return String(detail.message);
  return null;
}

/**
 * `savedFindings` is the value the form reads its saved findings from (an array or the drawing's
 * validation); a new reference means the drawing was read again.
 */
export function useDraftValidation(docId: string | null | undefined, enabled: boolean, savedFindings: unknown) {
  const [draft, setDraft] = useState<DraftValidation | null>(null);
  /** Why the server refuses the last body sent, as the matching Save would say it. */
  const [refusal, setRefusal] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const lastSentBodyRef = useRef<string | null>(null);
  /** Counts requests and drops; an answer is applied only while its number is still the latest. */
  const requestCountRef = useRef(0);
  const savedFindingsRef = useRef(savedFindings);
  savedFindingsRef.current = savedFindings;
  /** The saved findings at the last drawing write, while the draft stands in for the read after it. */
  const savedFindingsAtWriteRef = useRef<{ savedFindings: unknown } | null>(null);
  const { mutateAsync } = useMutation({ mutationFn: (request: DraftRequest) => request.send() });

  const discardDraft = useCallback(() => {
    requestCountRef.current += 1;
    lastSentBodyRef.current = null;
    savedFindingsAtWriteRef.current = null;
    setDraft(null);
    setRefusal(null);
    setIsValidating(false);
  }, []);

  const supersedeByWrite = useCallback(() => {
    requestCountRef.current += 1;
    lastSentBodyRef.current = null;
    savedFindingsAtWriteRef.current = { savedFindings: savedFindingsRef.current };
    setRefusal(null);
    setIsValidating(false);
  }, []);

  useEffect(() => (docId ? onDrawingWrite(docId, supersedeByWrite) : undefined), [docId, supersedeByWrite]);
  useEffect(() => {
    const atWrite = savedFindingsAtWriteRef.current;
    if (atWrite && atWrite.savedFindings !== savedFindings) discardDraft();
  }, [savedFindings, discardDraft]);
  useEffect(() => {
    if (!enabled) discardDraft();
  }, [enabled, discardDraft]);

  const requestDraft = useCallback((request: DraftRequest) => {
    const bodyKey = JSON.stringify(request.body);
    if (bodyKey === lastSentBodyRef.current) return;
    lastSentBodyRef.current = bodyKey;
    // This request reads the drawing as written, so its answer outranks a later read.
    savedFindingsAtWriteRef.current = null;
    requestCountRef.current += 1;
    const requestNumber = requestCountRef.current;
    setIsValidating(true);
    mutateAsync(request).then(
      (answer) => {
        if (requestNumber !== requestCountRef.current) return;
        setDraft(answer);
        setRefusal(null);
        setIsValidating(false);
      },
      (error: unknown) => {
        if (requestNumber !== requestCountRef.current) return;
        setDraft(null);
        setIsValidating(false);
        const message = refusalMessage(error);
        setRefusal(message);
        // A refused body stays the last one sent, so an unchanged blur does not ask again.
        // Any other failure lets the next blur retry.
        if (message === null) {
          lastSentBodyRef.current = null;
          console.error('Draft validation failed', error);
        }
      },
    );
  }, [mutateAsync]);

  return { draft, refusal, isValidating, requestDraft, discardDraft };
}

/** The input types the SME types free text into; every other control commits on change. */
const TYPED_INPUT_TYPES = new Set(['text', 'search', 'number', 'email', 'url', 'tel', 'password']);

function isTypedField(target: EventTarget): target is HTMLInputElement | HTMLTextAreaElement {
  return target instanceof HTMLTextAreaElement
    || (target instanceof HTMLInputElement && TYPED_INPUT_TYPES.has(target.type));
}

/**
 * Runs `validate` after each change of `formKey` that commits a value: a pick from a select or
 * a picker, a toggle, a checkbox. A change typed into a text field is not a commit: it waits
 * for that field's blur, so a draft goes out per field and not per keystroke.
 *
 * Typing into a field marks it. A pointer press, an Enter, or leaving the field ends the mark,
 * because every pick is one of those gestures: a picker that keeps focus in its input after a
 * mouse pick (the tag, Line Number and line-end pickers) still commits, and so does its filter
 * text followed by an Enter. Returns the handlers the form's container takes.
 *
 * A field's blur runs `validate` once the blur's own writes have rendered: a tag field rewrites
 * its value as it is left, and a draft built in the same event would carry the spelling it
 * replaced.
 */
export function useDraftOnCommit(formKey: string, validate: () => void) {
  const typedFieldRef = useRef<EventTarget | null>(null);
  const validateRef = useRef(validate);
  validateRef.current = validate;
  const blurTimerRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(blurTimerRef.current), []);

  // A change back to the values the form opened with runs `validate` too: the form then
  // discards its draft, and an answer still in flight for the edit it undid is dropped.
  useEffect(() => {
    if (typedFieldRef.current !== null && typedFieldRef.current === document.activeElement) return;
    validateRef.current();
  }, [formKey]);

  return {
    onInputCapture: (event: FormEvent) => {
      if (isTypedField(event.target)) typedFieldRef.current = event.target;
    },
    onPointerDownCapture: () => {
      typedFieldRef.current = null;
    },
    onKeyDownCapture: (event: KeyboardEvent) => {
      if (event.key === 'Enter') typedFieldRef.current = null;
    },
    onBlur: () => {
      window.clearTimeout(blurTimerRef.current);
      blurTimerRef.current = window.setTimeout(() => validateRef.current());
    },
  };
}
